import React from 'react';
import { Device } from '../../lib/types';
import type { RescueUnassignedInventoryDeviceItemGql } from '../../lib/rescueQueries';
import DeviceRow from './DeviceRow';
import UnassignedDeviceRow from './UnassignedDeviceRow';
import { SkeletonRows } from './SkeletonRow';
import Pagination from '../../components/Pagination/Pagination';
import Button from '../../components/Button/Button';
import styles from './Devices.module.scss';

const columns = ['Holder', 'Pet', 'Device', 'Setup', 'Status', 'Battery', 'Action'];

interface DevicesTableProps {
  rescueDevices: Device[];
  fosterDevices: Device[];
  unassignedDevices: RescueUnassignedInventoryDeviceItemGql[];
  rescueTotalCount: number;
  fosterTotalCount: number;
  rescueCurrentPage: number;
  rescueTotalPages: number;
  fosterCurrentPage: number;
  fosterTotalPages: number;
  pageSize: number;
  onRescuePageChange: (page: number) => void;
  onFosterPageChange: (page: number) => void;
  rescueLoading: boolean;
  fosterLoading: boolean;
  unassignedLoading: boolean;
  unassignedHasError: boolean;
  onRetryUnassigned: () => void;
  rescueSkeletonCount: number;
  fosterSkeletonCount: number;
  searchQuery: string;
  /** Pet IDs whose adoption transfer is complete — used to hide rescue-row actions. */
  adoptedPetIds?: Set<string>;
  /** True while the adopted-pet lookup is in flight — gate actions optimistically. */
  adoptedLoading?: boolean;
  onAddPet: (device: Device) => void;
  onAssignUnassigned: (moduleId: string) => void;
  onTransferFoster: (device: Device) => void;
  onRecall: (device: Device) => void;
  onTransferAdopter: (device: Device) => void;
  onSendSms: (device: Device) => void;
}

export default function DevicesTable({
  rescueDevices,
  fosterDevices,
  unassignedDevices,
  rescueTotalCount,
  fosterTotalCount,
  rescueCurrentPage,
  rescueTotalPages,
  fosterCurrentPage,
  fosterTotalPages,
  pageSize,
  onRescuePageChange,
  onFosterPageChange,
  rescueLoading,
  fosterLoading,
  unassignedLoading,
  unassignedHasError,
  onRetryUnassigned,
  rescueSkeletonCount,
  fosterSkeletonCount,
  searchQuery,
  adoptedPetIds,
  adoptedLoading,
  onAddPet,
  onAssignUnassigned,
  onTransferFoster,
  onRecall,
  onTransferAdopter,
  onSendSms,
}: DevicesTableProps) {
  const unassignedCount = unassignedDevices.length;
  const totalInStock = rescueTotalCount + fosterTotalCount + unassignedCount;
  const colCount = columns.length;
  const rowProps = {
    onAddPet,
    onTransferFoster,
    onRecall,
    onTransferAdopter,
    onSendSms,
    adoptedPetIds,
    adoptedLoading,
  };
  const showUnassigned = unassignedHasError || !unassignedLoading || unassignedCount > 0;

  // Use the dimming overlay when we have stale data to keep visible (prevents height jump).
  // Only fall back to skeleton rows when there's truly no data at all.
  const rescueHasData = rescueDevices.length > 0;
  const fosterHasData = fosterDevices.length > 0;
  const showOverlay = (rescueSkeletonCount > 0 && rescueHasData) || (fosterSkeletonCount > 0 && fosterHasData);

  const wrapperClass = `${styles.tableWrapper}${showOverlay ? ` ${styles.tableTransitioning}` : ''}`;

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Devices in Stock ({totalInStock})</h3>
        <p className={styles.sectionDescription}>
          Devices delivered to your rescue and currently in your inventory. Includes devices attached to pets in your
          rescue&rsquo;s household.
        </p>
      </div>
      <div className={wrapperClass}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: '14%' }} /> {/* Holder */}
            <col style={{ width: '13%' }} /> {/* Pet */}
            <col style={{ width: '14%' }} /> {/* Device */}
            <col style={{ width: '10%' }} /> {/* Setup */}
            <col style={{ width: '12%' }} /> {/* Status */}
            <col style={{ width: '10%' }} /> {/* Battery */}
            <col style={{ width: '27%' }} /> {/* Action */}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Unassigned group — devices not yet attached to a pet */}
            {showUnassigned && (
              <>
                <tr className={styles.subheaderRow}>
                  <td colSpan={colCount}>
                    {unassignedHasError ? (
                      <>Unassigned</>
                    ) : (
                      <>
                        Unassigned &mdash; {unassignedCount} device{unassignedCount !== 1 ? 's' : ''}
                      </>
                    )}
                  </td>
                </tr>
                {unassignedHasError ? (
                  <tr>
                    <td colSpan={colCount} className={styles.errorRow} role="alert">
                      <span className={styles.errorRowContent}>
                        <span>Could not load inventory.</span>
                        <Button variant="ghost" size="sm" onClick={onRetryUnassigned}>
                          Retry
                        </Button>
                      </span>
                    </td>
                  </tr>
                ) : unassignedCount === 0 ? (
                  <tr>
                    <td colSpan={colCount} className={styles.emptyState}>
                      No unassigned devices
                    </td>
                  </tr>
                ) : (
                  unassignedDevices.map((d) => (
                    <UnassignedDeviceRow key={d.moduleId} device={d} onAssign={onAssignUnassigned} />
                  ))
                )}
              </>
            )}

            {/* Rescue group — always shown */}
            <tr className={styles.subheaderRow}>
              <td colSpan={colCount}>
                Rescue &mdash; {rescueTotalCount} device{rescueTotalCount !== 1 ? 's' : ''}
              </td>
            </tr>
            {rescueSkeletonCount > 0 && !rescueHasData ? (
              <SkeletonRows count={rescueSkeletonCount} columns={colCount} />
            ) : rescueDevices.length === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No rescue devices matching "${searchQuery}"` : 'No rescue devices'}
                </td>
              </tr>
            ) : (
              rescueDevices.map((d) => <DeviceRow key={d.id} device={d} {...rowProps} />)
            )}
            {/* Rescue pagination */}
            {rescueTotalPages > 1 && (
              <tr className={styles.paginationRow}>
                <td colSpan={colCount}>
                  <Pagination
                    currentPage={rescueCurrentPage}
                    totalPages={rescueTotalPages}
                    totalItems={rescueTotalCount}
                    pageSize={pageSize}
                    onPageChange={onRescuePageChange}
                  />
                </td>
              </tr>
            )}

            {/* Foster group — always shown */}
            <tr className={styles.subheaderRow}>
              <td colSpan={colCount}>
                Fosters &mdash; {fosterTotalCount} device{fosterTotalCount !== 1 ? 's' : ''}
              </td>
            </tr>
            {fosterSkeletonCount > 0 && !fosterHasData ? (
              <SkeletonRows count={fosterSkeletonCount} columns={colCount} />
            ) : fosterDevices.length === 0 ? (
              <tr>
                <td colSpan={colCount} className={styles.emptyState}>
                  {searchQuery ? `No foster devices matching "${searchQuery}"` : 'No devices in foster'}
                </td>
              </tr>
            ) : (
              fosterDevices.map((d) => <DeviceRow key={d.id} device={d} {...rowProps} />)
            )}
            {/* Foster pagination */}
            {fosterTotalPages > 1 && (
              <tr className={styles.paginationRow}>
                <td colSpan={colCount}>
                  <Pagination
                    currentPage={fosterCurrentPage}
                    totalPages={fosterTotalPages}
                    totalItems={fosterTotalCount}
                    pageSize={pageSize}
                    onPageChange={onFosterPageChange}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
